import React, {FC, useEffect, useMemo, useState} from "react";
import {Workorder} from "../../../lib/src/api/dto/Workorder";
import {Employee} from "../../../lib/src/api/dto/Employee"
import {useApi} from "../api/APIContext";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload, faUser} from "@fortawesome/free-solid-svg-icons";
import {Button} from "@traas/lib/src/components/form/Button";
import {useApiCall} from "../api/api";
import {WorkorderFeedback} from "../../../lib/src/api/dto/WorkorderFeedback";
import {Task} from "@traas/lib/src/api/dto/Task";
import {QuantityTypeTemplate} from "@traas/lib/src/api/dto/QuantityTypeTemplate";
import {QuantityTypeTemplateQuestion} from "@traas/lib/src/api/dto/QuantityTypeTemplateQuestion";
import moment from "moment";

export const WorkorderDetailsModal: FC<{order: Workorder, task?: Task}> = (props) => {
  const {employees} = useApi()
  const {downloadWorkorderPdf, getTaskTemplate} = useApiCall()

  const [template, setTemplate] = useState<QuantityTypeTemplate|null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const data = await getTaskTemplate(props.task!.taskTemplateId!);
        setTemplate(data);
      } finally {
        setLoading(false);
      }
    };

    if (props.task != null && props.task.taskTemplateId != null) {
      fetchTemplate();
    }
  }, []);

  const employeesMap = useMemo(() => {
    return employees.reduce((acc, e) => {
      acc[e.id] = e
      return acc
    }, {} as {[key: string]: Employee})
  }, [employees])
  // Answers
  const questionAnswers = useMemo(() => props.order.feedback?.filter(f => f.answerType === 'questions'), [props.order])
  const [answerPage, setAnswerPage] = React.useState(0)
  useEffect(() => {
    if (questionAnswers && questionAnswers.length < (answerPage + 1)) {
      setAnswerPage(questionAnswers.length - 1)
    }
  }, [answerPage, questionAnswers]);
  // Fotos
  const questionImages = useMemo(() => props.order.feedback?.filter(f => f.answerType === 'image'), [props.order])
  const [imagePage, setImagePage] = React.useState(0)
  useEffect(() => {
    if (questionImages && questionImages.length < (imagePage + 1)) {
      setImagePage(questionImages.length - 1)
    }
  }, [imagePage, questionImages]);
  const downloadReport = async () => {
    const blob = await downloadWorkorderPdf(props.order.workorderId ?? '-')
    const file = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = file
    a.download = `werkbon.pdf`
    a.click()
    URL.revokeObjectURL(file)
  }
  return <>
    {/* Werkbon header */}
    <div className="my-4">
      <div className="flex items-center gap-4">
        <div className="flex-1 border border-slate-200 rounded-sm px-3 py-1">
          <div className="text-sm font-medium">Project #{props.order.projectNumber}</div>
          <div className="text-sm">{props.order.projectDescription ?? "Overig project"}</div>
          <div className="text-sm">{props.order.description}</div>
        </div>
        <div>
          <Button type="primary" size="md" text="Download" icon={faDownload} onClick={downloadReport}/>
        </div>
      </div>
    </div>

    {template != null ? <WorkorderQuestions questionFeedbacks={questionAnswers ?? []} photoFeedbacks={questionImages ?? []} template={template} employees={employeesMap} />
      : questionAnswers?.[answerPage] && <WorkorderDetailsQuestionFeedback feedback={questionAnswers?.[answerPage]} employeesMap={employeesMap} />
    }
    <WorkorderPhotos photoFeedbacks={questionImages ?? []} employees={employeesMap} />
  </>
}

const WorkorderQuestions: FC<{ questionFeedbacks: WorkorderFeedback[], photoFeedbacks: WorkorderFeedback[], template: QuantityTypeTemplate, employees: { [id: string]: Employee } }> = ({ questionFeedbacks, photoFeedbacks, template, employees }) => {
  const questionAnswersMap = useMemo(() => {
    const map = new Map<string, {
        employeeId: string;
        answer: string | boolean;
        photos: string[];
        answeredAt: Date;
      }[]>();

    questionFeedbacks.forEach((fb) => {
      fb.getQuestionAnswers().forEach((question) => {
        const existing = map.get(question.question) ?? [];

        existing.push({
          employeeId: fb.employeeId,
          answer: question.answer,
          photos: question.photos ?? [],
          answeredAt: fb.updatedAt
        });

        map.set(question.question, existing);
      });
    });

    return map;
  }, [questionFeedbacks]);

  return <div className={'my-4'}>
    <div className={'py-1 font-semibold'}>Vragen:</div>

    {template.questions.map((question, k) => {
        return <QuestionRow key={k} question={question} answers={questionAnswersMap.get(question.id)} photoFeedbacks={photoFeedbacks} employees={employees} />
    })}

  </div>
}

const QuestionRow: FC<{
  question: QuantityTypeTemplateQuestion,
  answers: {
    employeeId: string;
    answer: string | boolean;
    photos: string[];
    answeredAt: Date;
  }[] | undefined,
  employees: { [id: string]: Employee },
  photoFeedbacks: WorkorderFeedback[]
}> = ({ answers, photoFeedbacks, question, employees }) => {
  return <div className={'mb-8'}>
    <div className={'flex flex-row items-center space-x-2.5'}>
      <div className={'bg-blue-900 h-6 w-6 rounded-full flex justify-center items-center'}>
        <div className={'text-white text-sm font-medium'}>{question.order}</div>
      </div>
      <div className={'text-sm font-medium'}>{question.question}</div>
    </div>

    <div>
      {answers?.length === 0 || answers === undefined ? <></> : answers.map((answer, j) => {
        return <QuestionRowAnswer key={j} answer={answer} photoFeedbacks={photoFeedbacks} employeesMap={employees} />
      })}
    </div>
  </div>
}

const QuestionRowAnswer: FC<{
  answer: {
    employeeId: string;
    answer: string | boolean;
    photos: string[];
    answeredAt: Date;
  },
  employeesMap: { [id: string]: Employee },
  photoFeedbacks: WorkorderFeedback[]
}> = ({ answer, employeesMap, photoFeedbacks }) => {
  const employee = employeesMap[answer.employeeId]
  return <div className={'flex flex-row space-x-4 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 mt-3 mb-2'}>
    <div className="flex flex-1 flex-col space-y-2">
      {typeof answer.answer === 'boolean' ? <div>{answer.answer ? 'Ja' : 'Nee'}</div>
       : <div>{answer.answer}</div>
      }
      <div className={"grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"}>
        {answer.photos.map((photo, l) => {
          const image = photoFeedbacks.find((fbi) => fbi.id === photo)?.getUrl()
          return <ImageComp key={l} image={image ?? photo} />
        })}
      </div>
    </div>

    <div className={'flex flex-col items-end text-sm'}>
      <div>{`${employee.firstName} ${employee.lastName}`}</div>
      <div className="-mt-0.5 text-xs text-slate-500">{moment(answer.answeredAt).format("h:mm · DD MMMM YYYY")}</div>
    </div>
  </div>
}

const WorkorderPhotos: FC<{ photoFeedbacks: WorkorderFeedback[], employees: { [id: string]: Employee } }> = ({ photoFeedbacks }) => {
  return <>
    <div className={"my-4"}>
      <div className={"py-1 font-semibold"}>Foto's</div>

      <div className={"grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"}>
        {photoFeedbacks.map((photo, i) => (
          <ImageComp key={i} image={photo} />
        ))}
      </div>
    </div>
  </>
}

const ImageComp: FC<{ image: WorkorderFeedback|string }> = ({ image }) => {
  const imageUrl = typeof image === 'string' ? image : image.getUrl() ?? ""
  const [zoomed, setZoomed] = React.useState(false);

  return <>
      {zoomed && <>
        <div className={"fixed inset-0 z-50 flex items-center justify-center bg-black/80"} onClick={() => setZoomed(false)}>
          <img src={imageUrl} alt={''} className={"max-h-[90vh] max-w-[90vw] rounded-lg cursor-zoom-out"}/>
        </div>
      </>}

      <div className={"aspect-square overflow-hidden rounded-sm border border-slate-300 hover:border-2 hover:border-blue-800"} onClick={() => setZoomed(true)}>
        <img src={imageUrl} alt={''} className={"h-full w-full object-cover transition-transform duration-200 hover:scale-105"}/>
      </div>
    </>
}

const WorkorderDetailsQuestionFeedback: FC<{
  feedback: WorkorderFeedback,
  employeesMap: { [id: string]: Employee }
}> = props => {
  const employee = props.employeesMap[props.feedback.employeeId]
  const answers = ('questions' in props.feedback.answer ? props.feedback.answer['questions'] : []) as {
    answer: string,
    question: string
  }[]
  return <div>
    <div className={"text-blue-900 ml-2"}>
      <FontAwesomeIcon icon={faUser} className={"mr-2"}/>
      {employee?.firstName} {employee?.lastName}
    </div>
    <dl>
      {answers.map((a, k) => {

        // This is an ugly fix, that will work for the time being.
        // this will be fixed in this ticket when templates are added
        // https://app.windesk.nl/ventum-groep/ticket/0bde5e1c-a521-41b8-8877-c743f8001df7
        if (a.question === 'Verbruik in liters') {
          const usageInLiters = Math.round((parseFloat(a.answer) / 2.1) * 10) / 10;
          return <React.Fragment key={k}>
            <dt className={"font-medium mt-2"}>Verbruik in kg</dt>
            <dd className={"ml-2 text-sm text-slate-700"}>{`${a.answer}kg (${usageInLiters} liter)`}</dd>
          </React.Fragment>
        } else {
          return <React.Fragment key={k}>
            <dt className={"font-medium mt-2"}>{a.question}</dt>
            <dd className={"ml-2 text-sm text-slate-700"}>{a.answer}</dd>
          </React.Fragment>
        }
      })}
    </dl>
  </div>
}